import { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronUp, ChevronDown, X } from 'lucide-react';
import { useCompareStore } from '@/store';

export function CompareBar() {
  const { t } = useTranslation();
  const location = useLocation();
  const barRef = useRef<HTMLDivElement>(null);
  const {
    compareCars,
    isBarExpanded,
    toggleBarExpanded,
    setBarExpanded,
    removeFromCompare,
    clearCompare,
  } = useCompareStore();

  // Close when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (barRef.current && !barRef.current.contains(event.target as Node)) {
        setBarExpanded(false);
      }
    }

    if (isBarExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isBarExpanded, setBarExpanded]);

  const count = compareCars.length;

  // Don't show if no cars selected
  if (count === 0) {
    return null;
  }

  // Don't show on the compare page itself
  if (location.pathname === '/compare') {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50" ref={barRef}>
      {/* Compact floating compare bar */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-lg overflow-hidden" style={{ width: '360px' }}>
        {/* Header - always visible */}
        <button
          onClick={() => toggleBarExpanded()}
          className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
        >
          <div className="flex items-center gap-2">
            {isBarExpanded ? (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronUp className="w-4 h-4 text-gray-400" />
            )}
            <span className="text-gray-700 text-sm">
              {t('compare.myComparison', 'Mijn vergelijking')}
            </span>
          </div>
          <span className="w-6 h-6 rounded-full bg-orange-500 text-white text-xs font-bold flex items-center justify-center">
            {count}
          </span>
        </button>

        {/* Expanded Content */}
        {isBarExpanded && (
          <div className="border-t border-gray-100">
            {/* Car List */}
            <div className="max-h-64 overflow-y-auto">
              {compareCars.map((car) => (
                <div
                  key={car.id}
                  className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 border-b border-gray-50 last:border-b-0"
                >
                  {/* Car Image */}
                  <img
                    src={car.images?.[0]?.thumbUrl || car.images?.[0]?.mediumUrl || car.images?.[0]?.url || '/images/car-placeholder.png'}
                    alt={`${car.make} ${car.model}`}
                    className="w-14 h-10 object-cover rounded flex-shrink-0"
                  />

                  {/* Car Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {car.make} {car.model}
                    </p>
                    <p className="text-xs text-gray-500">
                      {car.year} • ${car.price.toLocaleString()}
                    </p>
                  </div>

                  {/* Remove Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFromCompare(car.id);
                    }}
                    className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="p-3 bg-gray-50 border-t border-gray-100 flex gap-2">
              <Link
                to="/compare"
                className="flex-1 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium text-center rounded transition-colors"
              >
                {t('compare.startComparison', 'Vergelijk')}
              </Link>
              <button
                onClick={() => clearCompare()}
                className="px-3 py-2 text-gray-500 hover:text-gray-700 text-sm transition-colors"
              >
                {t('compare.clear', 'Wissen')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
